import { Box, ButtonBase, Typography } from "@mui/material";
import type { EChartsOption, SeriesOption } from "echarts";
import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import { formatMeasureWithUnit } from "../../utils/format";
import type { ConsumptionChartModel } from "./chartModel";

export const renderTooltipDot = (color: string | undefined) =>
	`<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${
		color ?? "transparent"
	};margin-right:6px"></span>`;

interface ChartTooltipItem {
	seriesName: string;
	value: number | null;
	color: string;
	axisLabel: string;
}

const getTooltipItems = (parameters: unknown): ChartTooltipItem[] => {
	if (!Array.isArray(parameters)) return [];
	return parameters.flatMap((entry): ChartTooltipItem[] => {
		if (typeof entry !== "object" || entry === null) return [];
		const record = entry as Record<string, unknown>;
		if (typeof record.seriesName !== "string") return [];
		return [
			{
				seriesName: record.seriesName,
				value: typeof record.value === "number" ? record.value : null,
				color: typeof record.color === "string" ? record.color : "transparent",
				axisLabel:
					typeof record.axisValueLabel === "string"
						? record.axisValueLabel
						: "",
			},
		];
	});
};

const getClickedSeriesName = (parameters: unknown): string | null => {
	if (typeof parameters !== "object" || parameters === null) return null;
	const seriesName = (parameters as Record<string, unknown>).seriesName;
	return typeof seriesName === "string" ? seriesName : null;
};

const groupMetersByAccount = (
	meterIds: string[],
	accountNumberByMeterId: Map<string, string>,
): {
	accountNumber: string | undefined;
	meterIds: string[];
}[] => {
	const groups: {
		accountNumber: string | undefined;
		meterIds: string[];
	}[] = [];
	for (const meterId of meterIds) {
		const accountNumber = accountNumberByMeterId.get(meterId);
		const openGroup = groups[groups.length - 1];
		if (openGroup !== undefined && openGroup.accountNumber === accountNumber)
			openGroup.meterIds.push(meterId);
		else groups.push({ accountNumber, meterIds: [meterId] });
	}
	return groups;
};

interface PeriodBarsChartProps {
	model: ConsumptionChartModel;
	highlightedBucket: number | null;
	highlightedMeterId?: string | null;
	showLegend?: boolean;
	onMeterFilter: (meterId: string) => void;
}

export const PeriodBarsChart = ({
	model,
	highlightedBucket,
	highlightedMeterId = null,
	showLegend = true,
	onMeterFilter,
}: PeriodBarsChartProps) => {
	const chartRef = useRef<ReactECharts>(null);

	const [hiddenMeterIds, setHiddenMeterIds] = useState<string[]>([]);

	const isCrowded = model.labels.length > 12;
	const legendGroups = groupMetersByAccount(
		model.meterIds,
		model.accountNumberByMeterId,
	);

	const focusedMeterId =
		highlightedMeterId !== null &&
		model.meterIds.includes(highlightedMeterId) &&
		!hiddenMeterIds.includes(highlightedMeterId)
			? highlightedMeterId
			: null;

	const toggleMeter = (meterId: string) =>
		setHiddenMeterIds((current) =>
			current.includes(meterId)
				? current.filter((hidden) => hidden !== meterId)
				: [...current, meterId],
		);
	const series: SeriesOption[] = model.meterIds.map((meterId) => ({
		name: model.getSeriesName(meterId),
		type: "bar",
		stack:
			model.getStackKey?.(meterId) ??
			(model.meterIds.length > 1 ? "meters" : undefined),
		cursor: "pointer",
		data: model.valuesByMeterId.get(meterId) ?? [],
		barMaxWidth: 42,
		barMinHeight: focusedMeterId === meterId ? 3 : undefined,
		itemStyle:
			focusedMeterId === null || focusedMeterId === meterId
				? undefined
				: { opacity: 0.22 },
	}));

	const options: EChartsOption = {
		animationDuration: 0,
		color: model.meterIds.map((meterId) => model.getMeterColor(meterId)),
		tooltip: {
			trigger: "axis",
			appendToBody: true,
			formatter: (parameters: unknown) => {
				const items = getTooltipItems(parameters);
				if (items.length === 0) return "Consumption unavailable";
				const total = items.reduce(
					(sum, item) => (item.value === null ? sum : sum + item.value),
					0,
				);
				const rows: string[] = [];
				let openAccount: string | undefined;
				for (const item of items) {
					const meterId = model.meterIdBySeriesName.get(item.seriesName);
					const accountNumber =
						meterId === undefined
							? undefined
							: model.accountNumberByMeterId.get(meterId);
					if (model.showAccountNumbers && accountNumber !== openAccount) {
						openAccount = accountNumber;
						rows.push(
							`<span style="color:#5B6670">${model.groupLabel ?? "Account"} ${accountNumber ?? "n/a"}</span>`,
						);
					}
					const indent = model.showAccountNumbers ? "&nbsp;&nbsp;" : "";
					const dot = renderTooltipDot(item.color);
					rows.push(
						`${indent}${dot}${
							meterId === undefined
								? item.seriesName
								: model.getMeterName(meterId)
						}: <strong>${formatMeasureWithUnit(item.value, model.unit)}</strong>`,
					);
				}
				return [
					`<strong>${items[0].axisLabel}</strong>`,
					...rows,
					...(model.showTooltipTotal === false
						? []
						: [
								`Total: <strong>${formatMeasureWithUnit(total, model.unit)}</strong>`,
							]),
				].join("<br/>");
			},
		},
		legend: {
			show: false,
			selected: Object.fromEntries(
				model.meterIds.map((meterId) => [
					model.getSeriesName(meterId),
					!hiddenMeterIds.includes(meterId),
				]),
			),
		},
		grid: {
			left: 80,
			right: 32,
			top: 48,
			bottom: isCrowded ? 48 : 28,
		},
		xAxis: {
			type: "category",
			data: model.labels,
			axisLabel: { hideOverlap: false, rotate: isCrowded ? 45 : 0 },
		},
		yAxis: { type: "value", name: model.axisName },
		series,
	};

	useEffect(() => {
		const instance = chartRef.current?.getEchartsInstance();
		if (!instance) return;
		if (highlightedBucket === null) {
			instance.dispatchAction({ type: "downplay" });
			instance.dispatchAction({ type: "hideTip" });
			return;
		}
		instance.dispatchAction({
			type: "highlight",
			dataIndex: highlightedBucket,
		});
		const anchorSeriesIndex = model.meterIds.findIndex(
			(meterId) =>
				!hiddenMeterIds.includes(meterId) &&
				(model.valuesByMeterId.get(meterId)?.[highlightedBucket] ?? null) !==
					null,
		);
		if (anchorSeriesIndex === -1) {
			instance.dispatchAction({ type: "hideTip" });
			return;
		}
		instance.dispatchAction({
			type: "showTip",
			seriesIndex: anchorSeriesIndex,
			dataIndex: highlightedBucket,
		});
	}, [highlightedBucket, hiddenMeterIds]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
				width: "100%",
			}}
		>
			<Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
				<ReactECharts
					ref={chartRef}
					option={options}
					notMerge
					opts={{ renderer: "svg" }}
					onEvents={{
						click: (parameters: unknown) => {
							const seriesName = getClickedSeriesName(parameters);
							const meterId =
								seriesName === null
									? undefined
									: model.meterIdBySeriesName.get(seriesName);
							if (meterId !== undefined) onMeterFilter(meterId);
						},
					}}
					style={{
						position: "absolute",
						inset: 0,
						height: "100%",
						width: "100%",
					}}
				/>
			</Box>
			{showLegend && (
				<Box
					component="ul"
					aria-label="Meters"
					sx={{
						display: "flex",
						flexWrap: "wrap",
						justifyContent: "center",
						listStyle: "none",
						m: 0,
						p: 0,
						pt: 0.5,
						rowGap: 1,
					}}
				>
					{legendGroups.map((group, index) => (
						<Box
							component="li"
							key={group.accountNumber ?? group.meterIds[0]}
							sx={{
								alignItems: "center",
								borderLeft: index === 0 ? 0 : "1px solid",
								borderColor: "divider",
								display: "flex",
								flexDirection: "column",
								gap: 0.25,
								minWidth: 0,
								px: 1.5,
							}}
						>
							{model.showAccountNumbers &&
								group.accountNumber !== undefined && (
									<Typography
										component="div"
										sx={{
											color: "#5B6670",
											fontSize: "0.7rem",
											lineHeight: 1.3,
										}}
									>
										{model.groupLabel ?? "Account"} {group.accountNumber}
									</Typography>
								)}
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 1.25,
									justifyContent: "center",
								}}
							>
								{group.meterIds.map((meterId) => {
									const isHidden = hiddenMeterIds.includes(meterId);
									return (
										<ButtonBase
											key={meterId}
											aria-pressed={!isHidden}
											onClick={() => toggleMeter(meterId)}
											sx={{
												borderRadius: 1,
												gap: 0.5,
												opacity: isHidden ? 0.35 : 1,
												px: 0.25,
											}}
										>
											<Box
												sx={{
													bgcolor: model.getMeterColor(meterId),
													borderRadius: "2px",
													flexShrink: 0,
													height: 10,
													width: 10,
												}}
											/>
											<Typography
												component="span"
												sx={{
													color: "#333",
													fontSize: "0.75rem",
													fontWeight: 500,
													lineHeight: 1.3,
												}}
											>
												{model.getMeterName(meterId)}
											</Typography>
										</ButtonBase>
									);
								})}
							</Box>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};
